import React, { Suspense, lazy } from 'react'; // Import Suspense and lazy
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useParams, useSearchParams } from 'react-router-dom'; // Import Outlet
import LoginPage from './pages/LoginPage'; // Keep static
import SignUpPage from './pages/SignUpPage'; // Keep static
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import CheckoutCancelPage from './pages/CheckoutCancelPage';
import SubscriptionRequiredPage from './pages/SubscriptionRequiredPage';
import GetStarted from './pages/GetStarted';
import SignupSuccess from './pages/SignupSuccess';
import { useAuth } from './context/AuthContext';
import MainLayout from './components/layout/MainLayout'; // Import MainLayout
import SubscriptionGuard from './components/common/SubscriptionGuard';
import SuperAdminGuard from './components/common/SuperAdminGuard';
import ConnectGuard from './components/common/ConnectGuard';
import CoreFeaturesGuard from './components/common/CoreFeaturesGuard';
import SafetyModulesGuard from './components/common/SafetyModulesGuard';
import OrgAdminGuard from './components/common/OrgAdminGuard';
import ErrorBoundary from './components/ErrorBoundary';

// Import Legal Pages (Keep static)
import LicenseAgreementPage from './pages/LicenseAgreementPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';

// Lazy load protected page components
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const AddIncidentPage = lazy(() => import('./pages/AddIncidentPage'));
const ManageHoursPage = lazy(() => import('./pages/ManageHoursPage'));
const ViewIncidentsPage = lazy(() => import('./pages/ViewIncidentsPage'));
const ViewIncidentPage = lazy(() => import('./pages/ViewIncidentPage'));
const EditIncidentPage = lazy(() => import('./pages/EditIncidentPage'));
const OshaLogsPage = lazy(() => import('./pages/OshaLogsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const ManageLocationsPage = lazy(() => import('./pages/ManageLocationsPage'));
const ManageProjectsPage = lazy(() => import('./pages/ManageProjectsPage'));
const AddObservationPage = lazy(() => import('./pages/AddObservationPage'));
const ViewObservationsPage = lazy(() => import('./pages/ViewObservationsPage'));
const ViewObservationPage = lazy(() => import('./pages/ViewObservationPage'));
const EditObservationPage = lazy(() => import('./pages/EditObservationPage'));
const LeadingIndicatorsPage = lazy(() => import('./pages/LeadingIndicatorsPage'));
const AddNearMissPage = lazy(() => import('./pages/AddNearMissPage'));
const ViewNearMissesPage = lazy(() => import('./pages/ViewNearMissesPage'));
const ViewNearMissPage = lazy(() => import('./pages/ViewNearMissPage'));
const EditNearMissPage = lazy(() => import('./pages/EditNearMissPage'));
const AddHazardPage = lazy(() => import('./pages/AddHazardPage'));
const ViewHazardsPage = lazy(() => import('./pages/ViewHazardsPage'));
const ViewHazardReportPage = lazy(() => import('./pages/ViewHazardReportPage'));
const EditHazardReportPage = lazy(() => import('./pages/EditHazardReportPage'));
const AddInspectionPage = lazy(() => import('./pages/AddInspectionPage'));
const ViewInspectionsPage = lazy(() => import('./pages/ViewInspectionsPage'));
const ViewInspectionPage = lazy(() => import('./pages/ViewInspectionPage'));
const EditInspectionPage = lazy(() => import('./pages/EditInspectionPage'));
const ManageTrainingsPage = lazy(() => import('./pages/ManageTrainingsPage'));
const TrainingDetailPage = lazy(() => import('./pages/TrainingDetailPage'));
const ManageEmployeesPage = lazy(() => import('./pages/ManageEmployeesPage'));
const PrintBadgesPage = lazy(() => import('./pages/PrintBadgesPage'));
const ManageEmployeeTrainingPage = lazy(() => import('./pages/ManageEmployeeTrainingPage'));
const AddSwaPage = lazy(() => import('./pages/AddSwaPage'));
const ViewSwaPage = lazy(() => import('./pages/ViewSwaPage'));
const ViewSingleSwaPage = lazy(() => import('./pages/ViewSingleSwaPage'));
const EditSwaPage = lazy(() => import('./pages/EditSwaPage'));
const ManageAuditTemplatesPage = lazy(() => import('./pages/ManageAuditTemplatesPage'));
const ConductAuditPage = lazy(() => import('./pages/ConductAuditPage'));
const ViewAuditsPage = lazy(() => import('./pages/ViewAuditsPage'));
const ViewAuditResultsPage = lazy(() => import('./pages/ViewAuditResultsPage'));
const EditAuditPage = lazy(() => import('./pages/EditAuditPage'));
const QuarterlyKpiReportPage = lazy(() => import('./pages/reports/QuarterlyKpiReportPage'));
const AnnualTrendsReportPage = lazy(() => import('./pages/reports/AnnualTrendsReportPage'));
const TrainingReportsPage = lazy(() => import('./pages/TrainingReportsPage'));
const MonthlyAuditCompletionReportPage = lazy(() => import('./pages/reports/MonthlyAuditCompletionReportPage'));
const SafetyMeetingsListPage = lazy(() => import('./pages/SafetyMeetingsListPage'));
const AddSafetyMeetingPage = lazy(() => import('./pages/AddSafetyMeetingPage'));
const EditSafetyMeetingPage = lazy(() => import('./pages/EditSafetyMeetingPage'));
const ViewSafetyMeetingPage = lazy(() => import('./pages/ViewSafetyMeetingPage'));
const ManageTrainingRequirementsPage = lazy(() => import('./pages/ManageTrainingRequirementsPage'));
const ReportsHubPage = lazy(() => import('./pages/reports/ReportsHubPage'));
const TrainingComplianceBreakdownReportPage = lazy(() => import('./pages/reports/TrainingComplianceBreakdownReportPage'));
const ManageCorrectiveActionsPage = lazy(() => import('./pages/ManageCorrectiveActionsPage')); // Added import
const SuperAdminDashboardPage = lazy(() => import('./pages/SuperAdminDashboardPage'));
const HelpPage = lazy(() => import('./pages/HelpPage'));

// Case Management
const DesignatedProvidersPage = lazy(() => import('./pages/DesignatedProvidersPage'));
const WhereToSendPage = lazy(() => import('./pages/WhereToSendPage'));
const PhysicalDemandsPage = lazy(() => import('./pages/PhysicalDemandsPage'));

// JSA/JHA Routes
const AddJsaPage = lazy(() => import('./pages/AddJsaPage'));
const ViewJsaListPage = lazy(() => import('./pages/ViewJsaListPage'));
const ViewJsaPage = lazy(() => import('./pages/ViewJsaPage'));
const EditJsaPage = lazy(() => import('./pages/EditJsaPage'));

// SDS Library
const SdsLibraryPage = lazy(() => import('./pages/SdsLibraryPage'));

// Admin pages
const ConnectManagementPage = lazy(() => import('./pages/admin/ConnectManagementPage'));

// QR Code Routes
const QrCodeSettingsPage = lazy(() => import('./pages/QrCodeSettingsPage'));

// Public Form Routes - No authentication required
const PublicFormPage = lazy(() => import('./pages/PublicFormPage'));
const PublicFormSuccessPage = lazy(() => import('./pages/PublicFormSuccessPage'));

// Public Resources Library Route - No authentication required
const PublicResourcesLibraryPage = lazy(() => import('./pages/PublicResourcesLibraryPage'));

// Public Designated Provider Lookup Route - No authentication required
const PublicProviderLookupPage = lazy(() => import('./pages/PublicProviderLookupPage'));

// Public Employee Profile Route - No authentication required
const PublicEmployeeProfilePage = lazy(() => import('./pages/PublicEmployeeProfilePage'));

// Public Training Delivery Route - No authentication required
const PublicTrainingDeliveryPage = lazy(() => import('./pages/PublicTrainingDeliveryPage'));
const PublicJsaEditPage = lazy(() => import('./pages/PublicJsaEditPage'));
const PublicJsaViewPage = lazy(() => import('./pages/PublicJsaViewPage'));

// SyncConnect Public Landing Page - No authentication required
const SyncConnectLandingPage = lazy(() => import('./pages/connect/SyncConnectLandingPage'));

// SyncConnect & Core Features pages
const ConnectedOrgsPage = lazy(() => import('./pages/connect/ConnectedOrgsPage'));
const ConnectedOrgDetailPage = lazy(() => import('./pages/connect/ConnectedOrgDetailPage'));
const ConnectSettingsPage = lazy(() => import('./pages/connect/ConnectSettingsPage'));
const ConnectAcceptPage = lazy(() => import('./pages/connect/ConnectAcceptPage'));
const CompanyDocumentsPage = lazy(() => import('./pages/CompanyDocumentsPage'));
const ResourcesLibraryPage = lazy(() => import('./pages/ResourcesLibraryPage'));
const MyConnectionsPage = lazy(() => import('./pages/connect/MyConnectionsPage'));

// Define a reusable loading component (or use the existing one)
const LoadingFallback = () => (
  <div className="flex justify-center items-center h-screen text-2xl">
    Loading Page...
  </div>
);

// Wrapper component for protected routes using MainLayout
const ProtectedRouteLayout = () => (
  <MainLayout>
    <Outlet /> {/* Nested routes will render here */}
  </MainLayout>
);

// Unauth visitors hitting /connect/accept/:token: preserve the token through login
// by redirecting to "/" with a returnTo query param.
const ConnectAcceptRedirect: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const target = token ? `/?returnTo=/connect/accept/${encodeURIComponent(token)}` : '/';
  return <Navigate to={target} replace />;
};

// After auth hydration, "/" normally redirects to dashboard/connected-orgs. If the
// URL carries a Connect-accept returnTo, honor it instead. Allowlist prefix only.
const AuthenticatedRootRedirect: React.FC<{ hasSafetyModules: boolean }> = ({ hasSafetyModules }) => {
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  if (returnTo && returnTo.startsWith('/connect/accept/')) {
    return <Navigate to={returnTo} replace />;
  }
  return <Navigate to={hasSafetyModules ? '/dashboard' : '/connected-orgs'} replace />;
};

function App() {
  const { session, loading, hasSafetyModules } = useAuth();

  // Use the initial loading state from useAuth
  if (loading) {
    return <LoadingFallback />; // Use the same fallback for initial auth loading
  }

  return (
    <Router>
      {/* Wrap Routes with Suspense */}
      <Suspense fallback={<LoadingFallback />}>
        <ErrorBoundary>
        <Routes>
          {/* Public Routes */}
          <Route
            path="/"
            element={!session ? <LoginPage /> : <AuthenticatedRootRedirect hasSafetyModules={hasSafetyModules} />}
          />
          <Route
            path="/signup"
            element={!session ? <SignUpPage /> : <Navigate to={hasSafetyModules ? '/dashboard' : '/connected-orgs'} replace />}
          />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/checkout-success" element={<CheckoutSuccessPage />} />
          <Route path="/checkout-cancel" element={<CheckoutCancelPage />} />
          <Route path="/subscription-required" element={<SubscriptionRequiredPage />} />
          
          {/* Public Checkout Routes */}
          <Route path="/get-started" element={<GetStarted />} />
          <Route path="/signup-success" element={<SignupSuccess />} />
          
          {/* Legal Pages */}
          <Route path="/license" element={<LicenseAgreementPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />

          {/* Public Form Routes - No authentication required */}
          <Route path="/submit/:token" element={<PublicFormPage />} />
          <Route path="/submit/success" element={<PublicFormSuccessPage />} />

          {/* Public Resources Library Route - No authentication required */}
          <Route path="/resources/:token" element={<PublicResourcesLibraryPage />} />

          {/* Public Designated Provider Lookup Route - No authentication required */}
          <Route path="/providers/:token" element={<PublicProviderLookupPage />} />

          {/* Public JSA Edit/View Routes - No authentication required */}
          <Route path="/jsa-edit/:token" element={<PublicJsaEditPage />} />
          <Route path="/jsa-view/:token" element={<PublicJsaViewPage />} />

          {/* Public Employee Profile Route - No authentication required */}
          <Route path="/employee/:qrToken" element={<PublicEmployeeProfilePage />} />

          {/* Public Training Delivery Route - No authentication required */}
          <Route path="/training/:token" element={<PublicTrainingDeliveryPage />} />

          {/* Public SyncConnect Landing Page - No authentication required. Renders for
              both unauthenticated and authenticated visitors (no redirect) */}
          <Route path="/connect" element={<SyncConnectLandingPage />} />

          {/* Protected Routes: Wrap with ProtectedRouteLayout and SubscriptionGuard */}
          <Route element={session ? <SubscriptionGuard><ProtectedRouteLayout /></SubscriptionGuard> : <Navigate to="/" replace />}>
            <Route path="/super-admin" element={<SuperAdminGuard><SuperAdminDashboardPage /></SuperAdminGuard>} />
            <Route path="/admin/connect-requests" element={<Navigate to="/admin/connect-management" replace />} />
            <Route path="/admin/connect-management" element={<SuperAdminGuard><ConnectManagementPage /></SuperAdminGuard>} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/add-incident" element={<AddIncidentPage />} />
            <Route path="/manage-hours" element={<OrgAdminGuard><ManageHoursPage /></OrgAdminGuard>} />
            <Route path="/view-incidents" element={<ViewIncidentsPage />} />
            <Route path="/incident/:id" element={<ViewIncidentPage />} />
            <Route path="/edit-incident/:incidentId" element={<EditIncidentPage />} />
            <Route path="/osha-logs" element={<OshaLogsPage />} />
            <Route path="/manage-locations" element={<OrgAdminGuard><ManageLocationsPage /></OrgAdminGuard>} />
            <Route path="/manage-projects" element={<OrgAdminGuard><ManageProjectsPage /></OrgAdminGuard>} />
            <Route path="/manage-employees" element={<OrgAdminGuard><ManageEmployeesPage /></OrgAdminGuard>} />
            <Route path="/employees/print-badges" element={<OrgAdminGuard><PrintBadgesPage /></OrgAdminGuard>} />
            <Route path="/manage-employee-training" element={<OrgAdminGuard><ManageEmployeeTrainingPage /></OrgAdminGuard>} />
            <Route path="/manage-trainings" element={<OrgAdminGuard><ManageTrainingsPage /></OrgAdminGuard>} />
            <Route path="/manage-trainings/:id" element={<OrgAdminGuard><TrainingDetailPage /></OrgAdminGuard>} />
            <Route path="/add-observation" element={<AddObservationPage />} />
            <Route path="/view-observations" element={<ViewObservationsPage />} />
            <Route path="/view-observation/:id" element={<ViewObservationPage />} />
            <Route path="/edit-observation/:id" element={<EditObservationPage />} />
            <Route path="/leading-indicators" element={<LeadingIndicatorsPage />} />
            <Route path="/add-near-miss" element={<AddNearMissPage />} />
            <Route path="/view-near-misses" element={<ViewNearMissesPage />} />
            <Route path="/near-misses/view/:id" element={<ViewNearMissPage />} />
            <Route path="/edit-near-miss/:nearMissId" element={<EditNearMissPage />} />
            <Route path="/add-hazard" element={<AddHazardPage />} />
            <Route path="/view-hazards" element={<ViewHazardsPage />} />
            <Route path="/hazard-reports/view/:id" element={<ViewHazardReportPage />} />
            <Route path="/edit-hazard-report/:hazardReportId" element={<EditHazardReportPage />} />
            {/* Inspection Routes */}
            <Route path="/add-inspection" element={<AddInspectionPage />} />
            <Route path="/view-inspections" element={<ViewInspectionsPage />} />
            <Route path="/view-inspection/:id" element={<ViewInspectionPage />} />
            <Route path="/edit-inspection/:inspectionId" element={<EditInspectionPage />} />

            {/* Audit Routes */}
            <Route path="/manage-audit-templates" element={<ManageAuditTemplatesPage />} />
            {/* TODO: Add Edit Audit Template Route: /edit-audit-template/:templateId */}
            <Route path="/conduct-audit" element={<ConductAuditPage />} />
            <Route path="/view-audits" element={<ViewAuditsPage />} />
            <Route path="/view-audit-results/:auditId" element={<ViewAuditResultsPage />} />
            <Route path="/edit-audit/:auditId" element={<EditAuditPage />} />

            {/* Stop Work Authority Routes */}
            <Route path="/add-swa" element={<AddSwaPage />} />
            <Route path="/view-swa" element={<ViewSwaPage />} />
            <Route path="/view-swa/:id" element={<ViewSingleSwaPage />} />
            <Route path="/edit-swa/:swaId" element={<EditSwaPage />} />

            {/* JSA/JHA Routes */}
            <Route path="/add-jsa" element={<AddJsaPage />} />
            <Route path="/view-jsa" element={<ViewJsaListPage />} />
            <Route path="/view-jsa/:id" element={<ViewJsaPage />} />
            <Route path="/edit-jsa/:jsaId" element={<EditJsaPage />} />

            {/* Report Routes */}
            <Route path="/reports/quarterly-kpi" element={<QuarterlyKpiReportPage />} />
            <Route path="/reports/annual-trends" element={<AnnualTrendsReportPage />} />
            {/* Route for Location Comparison removed */}
            <Route path="/reports/training" element={<OrgAdminGuard><TrainingReportsPage /></OrgAdminGuard>} />
            {/* New Reporting Routes */}
            <Route path="/reports" element={<ReportsHubPage />} />
            {/* EWS Route Removed */}
            {/* Indicator Correlation Route Removed */}
            <Route path="/reports/training-compliance" element={<TrainingComplianceBreakdownReportPage />} />
            <Route path="/reports/monthly-audit-completion" element={<MonthlyAuditCompletionReportPage />} />

            {/* Safety Meeting Routes */}
            <Route path="/safety-meetings" element={<SafetyMeetingsListPage />} />
            <Route path="/safety-meetings/add" element={<AddSafetyMeetingPage />} />
            <Route path="/safety-meetings/edit/:id" element={<EditSafetyMeetingPage />} />
            <Route path="/safety-meetings/view/:id" element={<ViewSafetyMeetingPage />} />

            {/* Add Manage Training Requirements Route */}
            <Route path="/training-requirements" element={<OrgAdminGuard><ManageTrainingRequirementsPage /></OrgAdminGuard>} />

            {/* Add Manage Corrective Actions Route */}
            <Route path="/manage-corrective-actions" element={<ManageCorrectiveActionsPage />} />

            {/* SDS Library Route */}
            <Route path="/sds-library" element={<SdsLibraryPage />} />

            {/* Company Documents — requires safety modules (paid/enterprise) */}
            <Route path="/company-documents" element={<CompanyDocumentsPage />} />

            {/* Resources Library Route */}
            <Route path="/resources-library" element={<OrgAdminGuard><ResourcesLibraryPage /></OrgAdminGuard>} />

            {/* Client Connections — sub's view of hiring-client approval status */}
            <Route path="/my-connections" element={<MyConnectionsPage />} />

            {/* Case Management: designated provider directory (Build #1, increment 1A) */}
            <Route path="/case-management/providers" element={<DesignatedProvidersPage />} />
            <Route path="/case-management/where-to-send" element={<WhereToSendPage />} />
            <Route path="/case-management/physical-demands" element={<PhysicalDemandsPage />} />
          </Route>

          {/* SyncConnect Routes — parallel guard, isolated from SubscriptionGuard */}
          <Route element={session ? <ConnectGuard><ProtectedRouteLayout /></ConnectGuard> : <Navigate to="/" replace />}>
            <Route path="/connected-orgs" element={<ConnectedOrgsPage />} />
            <Route path="/connected-orgs/:orgId" element={<ConnectedOrgDetailPage />} />
            <Route path="/connect/settings" element={<ConnectSettingsPage />} />
          </Route>

          {/* Core Features Routes — available to any entitled org type (paid OR Connect client) */}
          <Route element={session ? <CoreFeaturesGuard><ProtectedRouteLayout /></CoreFeaturesGuard> : <Navigate to="/" replace />}>
            <Route path="/help" element={<HelpPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/settings/qr-codes" element={<SafetyModulesGuard><QrCodeSettingsPage /></SafetyModulesGuard>} />
          </Route>

          {/* Public Connect invitation acceptance — requires session, no entitlement guard */}
          <Route
            path="/connect/accept/:token"
            element={session ? <ConnectAcceptPage /> : <ConnectAcceptRedirect />}
          />

          {/* Unified catch-all — authenticated users go to their natural landing page */}
          <Route
            path="*"
            element={
              session
                ? <Navigate to={hasSafetyModules ? '/dashboard' : '/connected-orgs'} replace />
                : <Navigate to="/" replace />
            }
          />
        </Routes>
        </ErrorBoundary>
      </Suspense>
    </Router>
  );
}

export default App;
